import { detect } from 'detect-browser'
import { useMemo} from 'react'
import UAParser from 'ua-parser-js'
import { GLOBAL } from '@/config/settings'
import { ErrorEnum } from '@/utils/announcements'
import { coerce, lt } from 'semver'
import {logger} from "@/logger/logger";
import {useAnnouncementsContext} from "@/contexts/AnnouncementsContext";

const versionLowerThan = (version: any, targetVersion: any): boolean => {
  try {
    return lt(coerce(version)!, coerce(targetVersion)!)
  } catch (_error) {
    return Number(version) < Number(targetVersion)
  }
}

const runCompatibilityCheck = (device: any, addAnnouncement: ReturnType<typeof useAnnouncementsContext>["addAnnouncement"]): void => {
  try {
    const RULES = Object.freeze({
      isSafariLessThan: (targetVersion: any) => {
        return device.browser.name === 'Safari' && versionLowerThan(device.browser.version, targetVersion)
      },
      isIOSLessThan: (targetVersion: any) => {
        return device.os.name === 'iOS' && versionLowerThan(device.os.version, targetVersion)
      },
      isAndroidLessThan: (targetVersion: any) => {
        return device.os.name === 'Android' && versionLowerThan(device.os.version, targetVersion)
      },
      isAmazonLessThan: (targetVersion: any) => {
        return (device.device.vendor === 'Amazon' && device.os.name === 'Android') && versionLowerThan(device.os.version, targetVersion)
      },
      isUnsupportedWebView: () => {
        return (device.name === 'chromium-webview' || ['Chrome WebView', 'Android Browser'].includes(device.browser.name)) && (device.engine.name === 'Blink' && versionLowerThan(device.engine.version, 60))
      },
      isUnsupportedBrowser: () => {
        return (device.engine.name === 'Blink' && versionLowerThan(device.engine.version, 60)) || (device.engine.name === 'WebKit' && versionLowerThan(device.engine.version, '604.2.4'))
      },
      isNotOfficiallySupported: () => {
        return (
          RULES.isSafariLessThan(12)
          || RULES.isAndroidLessThan(7)
          || ((device.engine.name === 'Blink' || ['Edge', 'Chrome'].includes(device.browser.name)) && versionLowerThan(device.engine.version, 80))
        )
      }
    })

    if (
      RULES.isSafariLessThan(11)
      || RULES.isIOSLessThan(11)
      || RULES.isAndroidLessThan('4.4')
      || RULES.isAmazonLessThan(5)
    ) {
      return addAnnouncement(ErrorEnum.DEVICE_NOT_SUPPORTED)
    }

    if (RULES.isUnsupportedWebView()) {
      return addAnnouncement(ErrorEnum.WEBVIEW_NOT_SUPPORTED)
    }

    if (RULES.isUnsupportedBrowser()) {
      return addAnnouncement(ErrorEnum.BROWSER_NOT_SUPPORTED)
    }

    const notSupportedString = 'not-supported'
    if (RULES.isNotOfficiallySupported()) {
      const notSupported = localStorage.getItem(notSupportedString)

      if (notSupported !== device.ua) {
        addAnnouncement(ErrorEnum.NOT_OFFICIALLY_SUPPORTED)
      }

      return localStorage.setItem(notSupportedString, device.ua)
    } else {
      return localStorage.removeItem(notSupportedString)
    }
  } catch (error) {
    logger.error('Device detection failed', error)
    // setGlobalError(error)
  }
}

export const detectDevice = (addAnnouncement: ReturnType<typeof useAnnouncementsContext>["addAnnouncement"]): any => {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined
  const deviceRegexes: any[] = [
    [/(Android\s\d\.?\d+?\.?\d+?;\s)(kf[A-z]+)(\sbuild\/|\))(.+silk\/)?/i],
    [
      UAParser.DEVICE.MODEL,
      UAParser.DEVICE.MODEL,
      [UAParser.DEVICE.VENDOR, 'Amazon'],
      [UAParser.DEVICE.TYPE, UAParser.DEVICE.TABLET]
    ]
  ]

  const parser = new UAParser({ device: deviceRegexes })

  const getDevice = (ua = navigator.userAgent) => ({
    ...detect(ua),
    ...parser.setUA(ua).getResult(),
    isElectron: navigator.userAgent.toLowerCase().includes('electron')
  })

  const device = (GLOBAL as any).device = getDevice(userAgent)

  runCompatibilityCheck(device, addAnnouncement)

  return device
}

export const useDevice = () => {
  const { addAnnouncement } = useAnnouncementsContext()
  return useMemo(() => detectDevice(addAnnouncement), [])
}

export default useDevice
